import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNav, useMargaret } from '@koob/margaret';
import { KoobNavbarUserDropdown, KoobOrganizationPicker } from '@koob/ui';
import { switchActingOrganization } from '@koob/utils';
import NotificationsDropdown from 'components/NotificationsDropdown';
import { useApp, useAuth } from 'hooks';

const roleToTranslationKey = (role) =>
  role?.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

// The app's global bar, above every page header, as in TO: the menu button on
// phones and the organization picker on the left; the trip designer's trip
// header (portalled into the slot on compose); the user menu and, last,
// notifications. Same
// height as the sidebar's logo strip (h-20). Page titles, crumbs and actions
// live in KoobPageHeader.
const TopBar = () => {
  const { t } = useTranslation('mainSidebar');
  const navigate = useNavigate();
  const { isMobile } = useMargaret();
  const {
    user,
    currentUserRole,
    currentOrganization,
    currentOrganizationIdInt,
    isAllowedTo,
  } = useApp();
  const { logout, impersonating, stopImpersonating } = useAuth();

  const roleKey = roleToTranslationKey(currentUserRole);

  return (
    <div className="relative z-navbar flex h-20 items-center gap-4 border-b border-gray-200 bg-white px-4 sm:px-6">
      {isMobile && <MainNav.Trigger />}
      <KoobOrganizationPicker
        className="w-40 shrink-0 sm:w-56"
        organizations={user?.organizations ?? []}
        currentOrganizationId={currentOrganization?.organizationId}
        onChange={switchActingOrganization}
        label={t('organizationPicker')}
        groupLabel={(name) => t('allOrganizationsOf', { name })}
      />

      {/* KD-3391: on compose, the page portals its trip header
          (ComposeHeader) into this slot */}
      <div id="koob-navbar-trip-designer-slot" className="min-w-0 flex-auto" />

      <KoobNavbarUserDropdown
        name={
          user?.firstName
            ? `${user.firstName} ${user.lastName || ''}`
            : user?.email
        }
        email={user?.email}
        organization={
          currentOrganization?.displayName || user?.organizationName
        }
        role={roleKey ? t(`user:roles.${roleKey}`) : undefined}
        impersonating={impersonating}
        profileLabel={t('myProfile')}
        organizationLabel={t('myOrganization')}
        auditLogLabel={t('auditLog')}
        supportLabel={t('userGuide')}
        logoutLabel={t('signOut')}
        impersonatingLabel={t('impersonation:status')}
        impersonatingBannerLabel={t('impersonation:actingAs')}
        stopImpersonatingLabel={t('impersonation:stop')}
        onStopImpersonating={stopImpersonating}
        onProfile={() => navigate('/user')}
        onOrganization={
          currentOrganizationIdInt && isAllowedTo('readSelfOrganization')
            ? () => navigate(`/organizations/${currentOrganizationIdInt}`)
            : undefined
        }
        onAuditLog={
          isAllowedTo('readAuditLog')
            ? () => navigate('/settings/audit-log')
            : undefined
        }
        onSupport={() => window.open('https://support.koob.tech/en/', '_blank')}
        onLogout={logout}
      />

      <NotificationsDropdown />
    </div>
  );
};

export default TopBar;
